import React from 'react';

import Typography from '@mui/material/Typography';

import HandsonArticleLayout from './components/HandsonArticleLayout';
import WorkflowChatBubble from './components/WorkflowChatBubble';
import WorkflowChecklist from './components/WorkflowChecklist';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowDirectoryTree from './components/WorkflowDirectoryTree';
import WorkflowGoalBox from './components/WorkflowGoalBox';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLinkCard from './components/WorkflowLinkCard';
import WorkflowList from './components/WorkflowList';
import WorkflowLockedCodeBlock from './components/WorkflowLockedCodeBlock';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const seriesSteps = [
  {
    label: 'プロジェクト作成',
    title: 'プロジェクト作成 + Steeringルール設置',
    active: true,
  },
  {
    label: 'API仕様書取込',
    title: 'API仕様書の取り込み',
    path: '/devdocs/handson/api-specs',
  },
  {
    label: '画面入出力定義書',
    title: '画面入出力定義書の作成',
    path: '/devdocs/handson/screen-io',
  },
  { label: '画面実装', title: '画面実装', path: '/devdocs/handson/implementation' },
];

const HandsonProjectSetup: React.FC = () => {
  return (
    <HandsonArticleLayout
      title="フロントエンド編 — プロジェクト作成 + Steeringルール設置"
      seriesName="フロントエンド編"
      tags={['Hands-on', 'Frontend', 'Vite', 'React', 'Setup']}
      seriesSteps={seriesSteps}
      prevLink={{
        label: 'E2Eテスト',
        path: '/devdocs/handson/e2e',
      }}
      nextLink={{
        label: 'API仕様書の取り込み',
        path: '/devdocs/handson/api-specs',
      }}
    >
      <WorkflowGoalBox
        prerequisites={[
          '「実装編 — E2Eテスト」が完了している',
          'Node.js（v18以上）がインストールされている',
          'npmコマンドが使える状態になっている',
        ]}
        completionCriteria={[
          'Vite + React + TypeScriptのプロジェクトが作成されている',
          'MUI / React Router / TanStack Queryがインストールされている',
          'HELPME.mdがKiroによって生成されている',
          'npm run dev で開発サーバーが起動し、ブラウザで画面が表示される',
          '.kiro/steering/ にproduct.md / tech.md / structure.mdが配置されている',
          'GitHubにリポジトリを作成し、初回コミット・プッシュが完了している',
        ]}
      >
        フロントエンド開発の基盤を整える。Vite + React +
        TypeScriptのプロジェクトを作成し、Steeringルールを設置して、AIによる画面実装の準備を完了する。
      </WorkflowGoalBox>

      <WorkflowLinkCard
        title="実装編 — E2Eテスト"
        description="前の記事"
        path="/devdocs/handson/e2e"
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: 'フロントエンドに挑戦する' },
          { id: 'section-2', label: 'これから作るもの' },
          { id: 'section-3', label: 'プロジェクトを作成する' },
          { id: 'section-4', label: 'Steeringルールを設置する' },
          { id: 'section-5', label: '現在のプロジェクト構成' },
          { id: 'section-6', label: 'Gitリポジトリを作成してプッシュする' },
        ]}
      />

      {/* セクション1: フロントエンドに挑戦する */}
      <WorkflowSection title="フロントエンドに挑戦する" id="section-1" number={1}>
        <WorkflowText>
          ここからはフロントエンド編です。バックエンドで作ったAPIに対して、ユーザーが実際に操作する画面を作っていきます。
        </WorkflowText>

        <WorkflowInfoBox type="info">
          <Typography variant="body2" fontWeight={600}>
            React未経験でも大丈夫です。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            このハンズオンではUI実装の大部分をAIに任せます。あなたがやることは「何を表示したいか」「どう動いてほしいか」を定義書に書くことです。コードを手で書く場面はほぼありません。
          </Typography>
        </WorkflowInfoBox>

        <WorkflowText>フロントエンド編は以下の方針で進めます。</WorkflowText>

        <WorkflowTable
          headers={['対象', '方針']}
          rows={[
            [
              'React未経験者',
              'MUI（UIライブラリ）を使用。デザインやコンポーネント選定はAIに任せる',
            ],
            ['React経験者', 'MUI不使用も可。ただし実装はAIに全て任せること（手書き禁止）'],
          ]}
        />

        <WorkflowTipBox title="なぜ経験者も手書き禁止なのか？">
          <WorkflowText>
            このハンズオンの目的は「AIに実装を任せる技術」を身につけることです。経験者にとってReactのコードを手で書くのは簡単ですが、それではハンズオンの学びが半減します。
          </WorkflowText>
          <WorkflowText>
            経験者が挑戦すべきは「自分の頭の中にあるデザインを、AIに正確に伝えて再現させる」ことです。MUIを使わない場合、後述のdesign_guideline.mdや画面入出力定義書でデザインの指示をより詳細に書く必要があり、むしろ難易度は上がります。
          </WorkflowText>
        </WorkflowTipBox>
      </WorkflowSection>

      {/* セクション2: これから作るもの */}
      <WorkflowSection title="これから作るもの" id="section-2" number={2}>
        <WorkflowText>
          フロントエンドプロジェクトで使用する技術スタックと、それぞれの役割を確認します。
        </WorkflowText>

        <WorkflowTable
          headers={['ライブラリ', '役割', 'なぜ使うか']}
          rows={[
            [
              'Vite',
              'ビルドツール',
              '高速な開発サーバーとビルドを提供。プロジェクトの雛形作成もこれで行う',
            ],
            ['React', 'UIフレームワーク', 'コンポーネント単位でUIを構築する。AIとの相性が良い'],
            [
              'TypeScript',
              '言語',
              '型があることでAIの生成コードの品質が上がり、エラーも発見しやすい',
            ],
            [
              'MUI (Material UI)',
              'UIライブラリ',
              'Googleのマテリアルデザインに基づいたコンポーネント集。デザインスキル不要で見栄えの良い画面が作れる',
            ],
            ['React Router', 'ルーティング', '画面遷移（URL切り替え）を管理する'],
            [
              'TanStack Query',
              'API通信・状態管理',
              'バックエンドAPIとの通信とデータのキャッシュ管理を担当する',
            ],
          ]}
        />

        <WorkflowInfoBox type="info">
          <Typography variant="body2" fontWeight={600}>
            ポイント
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            全てを理解する必要はありません。各ライブラリの「役割」だけ把握しておけば、AIに実装を指示するときに困りません。
          </Typography>
        </WorkflowInfoBox>
      </WorkflowSection>

      {/* セクション3: プロジェクトを作成する */}
      <WorkflowSection title="プロジェクトを作成する" id="section-3" number={3}>
        <WorkflowSection title="プロジェクトディレクトリを作成する" subNumber="3.1">
          <WorkflowText>
            ターミナルを開き、バックエンドプロジェクト（team-todo）と同じ階層にフロントエンド用のディレクトリを作成します。
          </WorkflowText>

          <WorkflowCodeBlock language="bash" code={`mkdir team_todo_front`} />

          <WorkflowText>ディレクトリ構成のイメージ:</WorkflowText>

          <WorkflowDirectoryTree
            root="your-workspace/"
            entries={[
              { name: 'team-todo', desc: 'バックエンド（既存）' },
              { name: 'team_todo_front', desc: 'フロントエンド（今回作成）', highlight: '#e65100' },
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="Kiroで新しいワークスペースとして開く" subNumber="3.2">
          <WorkflowText>
            Kiroで team_todo_front ディレクトリを新しいウィンドウで開きます。
          </WorkflowText>

          <WorkflowList
            variant="ordered"
            items={[
              'Kiroのメニュー: File > Open Folder',
              '先ほど作成した team_todo_front ディレクトリを選択',
              '新しいウィンドウで開く',
            ]}
          />

          <WorkflowInfoBox type="important">
            <Typography variant="body2">
              <strong>重要</strong>:
              バックエンドと同じウィンドウで開かず、必ず新しいウィンドウ（新しいワークスペース）として開いてください。Steeringルールはワークスペース単位で適用されるため、別ウィンドウにする必要があります。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="Kiroにプロジェクトを作成してもらう" subNumber="3.3">
          <WorkflowText>Kiroのチャットに以下のプロンプトを入力します。</WorkflowText>

          <WorkflowChatBubble role="human">
            {`このディレクトリにVite + React + TypeScriptのプロジェクトを作成してください。

要件:
- Viteでプロジェクトを初期化する（テンプレート: react-ts）
- 非対話式のコマンドで実行する（プロンプトで質問が出ないようにする）
- カレントディレクトリ直下にファイルを展開する（サブディレクトリを作らない）
- プロジェクト初期化後、以下のライブラリを追加インストールする:
  - @mui/material @emotion/react @emotion/styled（MUI本体）
  - @mui/icons-material（MUIアイコン）
  - react-router-dom（ルーティング）
  - @tanstack/react-query（API通信・状態管理）

追加でHELPME.mdを作成してください:
- 開発サーバーの起動方法（npm run dev）
- ビルド方法（npm run build）
- 各ライブラリの簡単な説明と公式ドキュメントへのリンク
- よくあるトラブルと対処法（ポート競合、node_modules消失時の対応等）`}
          </WorkflowChatBubble>

          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              ポイント
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              「非対話式のコマンドで実行する」を指定しているのは、Kiroがターミナルでコマンドを実行する際に対話プロンプト（&quot;Ok
              to proceed?
              (y)&quot;等）が出ると処理が止まるためです。AIにコマンドを実行させるときは、可能な限り非対話式オプションを指定しましょう。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="動作確認" subNumber="3.4">
          <WorkflowText>
            Kiroが生成した HELPME.md に記載されている手順で開発サーバーを起動します。
          </WorkflowText>

          <WorkflowCodeBlock language="bash" code={`npm run dev`} />

          <WorkflowText>
            ターミナルに表示されるURL（通常は http://localhost:5173
            ）をブラウザで開きます。紫色の背景にViteとReactのロゴが並び、中央に「Vite +
            React」の見出しとカウンターボタンが表示されれば成功です。ボタンをクリックするとカウントが増えるので、試しに何回か押してみてください。
          </WorkflowText>

          <WorkflowInfoBox type="success">
            <Typography variant="body2" fontWeight={600}>
              成功！ フロントエンドプロジェクトの雛形が完成しました。
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              うまくいかない場合はHELPME.mdの「よくあるトラブルと対処法」を確認してください。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション4: Steeringルールを設置する */}
      <WorkflowSection title="Steeringルールを設置する" id="section-4" number={4}>
        <WorkflowText>
          AI駆動開発の品質はSteeringルールで決まります。フロントエンド側にも必要なルールを設置しましょう。
        </WorkflowText>

        <WorkflowSection title=".kiro/steering ディレクトリを作成する" subNumber="4.1">
          <WorkflowText>
            プロジェクトルートに .kiro/steering/ ディレクトリを作成します。
          </WorkflowText>
          <WorkflowCodeBlock language="bash" code={`mkdir -p .kiro/steering`} />
        </WorkflowSection>

        <WorkflowSection title="product.md をコピーする" subNumber="4.2">
          <WorkflowText>
            バックエンドプロジェクトで作成した product.md を、フロントエンド側の .kiro/steering/
            にコピーします。
          </WorkflowText>

          <WorkflowCodeBlock
            language="bash"
            code={`cp ../team-todo/.kiro/steering/product.md .kiro/steering/product.md`}
          />

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>:
              パスはあなたの環境に合わせて変更してください。バックエンドプロジェクトが
              team_todo_front と同じ階層にある場合は上記のパスで問題ありません。
            </Typography>
          </WorkflowInfoBox>

          <WorkflowTipBox title="なぜコピーするのか？ マルチルートワークスペースではダメなのか？">
            <WorkflowText>
              VS
              Code（Kiro）には「マルチルートワークスペース」という機能があり、複数のリポジトリを1つのウィンドウで開くことができます。これを使えばバックエンドの
              product.md を直接参照できそうに見えますが、現時点ではおすすめしません。
            </WorkflowText>
            <WorkflowText>
              理由は、Kiroの仕様としてSteeringルールは「編集対象のファイルが属するワークスペース」のものが適用されるためです。マルチルートワークスペースでAのフォルダからBのフォルダのファイルを編集しても、B側のSteeringルールが適用されません。結果として、別ワークスペースのファイルは参照専用としてしか使えず、ファイルを相対パスで参照するのとあまり変わりません。
            </WorkflowText>
            <WorkflowText>
              今後Kiroの仕様が変わる可能性はありますが、現時点では「リポジトリごとにワークスペースを分け、共有ドキュメントはコピーして使う」方針が安全です。
            </WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>

        <WorkflowSection title="tech.md を作成する" subNumber="4.3">
          <WorkflowText>
            フロントエンド用の技術スタック・コーディング規約を定義する tech.md を作成します。
          </WorkflowText>
          <WorkflowText>
            バックエンドのときは自分で考えて書きましたが、フロントエンドはReact未経験の方も多いため、今回は完成例をそのまま使います。以下の完成例をコピーして
            .kiro/steering/tech.md
            として保存してください。React経験者は自分で考えて書いても構いません。
          </WorkflowText>

          <WorkflowTipBox title="tech.mdには何が書いてあるのか？">
            <WorkflowText>
              tech.mdはAIに「このプロジェクトではどんな技術を使い、どんなルールでコードを書くか」を伝えるファイルです。バックエンドで作ったtech.md（Java/Spring
              Boot向け）のフロントエンド版にあたります。
            </WorkflowText>
            <WorkflowText>
              主な内容: 技術スタック（Vite / React / TypeScript / MUI
              等のバージョン）、コーディング規約（命名規則、コンポーネント設計方針）、ファイル構成のルール、AIが生成するコードの品質基準。
            </WorkflowText>
          </WorkflowTipBox>

          <WorkflowLockedCodeBlock password="Qw7nBx" showPassword>
            {`---
inclusion: always
---

# 技術スタック

| カテゴリ | 技術 | バージョン |
|----------|------|-----------|
| ビルドツール | Vite | 6.x |
| 言語 | TypeScript | 5.x |
| UIフレームワーク | React | 19.x |
| UIライブラリ | MUI (Material UI) | 6.x |
| ルーティング | React Router | 7.x |
| API通信 | TanStack Query | 5.x |
| パッケージマネージャ | npm | - |

# コーディング規約

## 基本ルール

- 全てのコンポーネントは関数コンポーネント + TypeScriptで記述する
- \`any\` 型の使用は禁止。必ず適切な型を定義する
- コンポーネントのpropsには必ずinterfaceを定義する
- 未使用のimportやコメントアウトされたコードは残さない

## 命名規則

| 対象 | 規則 | 例 |
|------|------|-----|
| コンポーネント | PascalCase | \`TodoList\`, \`TaskCard\` |
| 関数・変数 | camelCase | \`fetchTodos\`, \`isLoading\` |
| 型・interface | PascalCase | \`Todo\`, \`CreateTodoRequest\` |
| 定数 | UPPER_SNAKE_CASE | \`API_BASE_URL\` |
| ファイル名（コンポーネント） | PascalCase.tsx | \`TodoList.tsx\` |
| ファイル名（その他） | camelCase.ts | \`apiClient.ts\` |

## コンポーネント設計

- 1ファイル1コンポーネントを原則とする
- ページコンポーネントと部品コンポーネントを分離する
- ビジネスロジックはカスタムフックに切り出す
- MUIコンポーネントを優先的に使用し、独自CSSは最小限にする

## API通信

- TanStack Queryを使用する。直接fetchやaxiosを呼ばない
- APIクライアントは共通モジュールに集約する
- エラーハンドリングは共通で行う`}
          </WorkflowLockedCodeBlock>
        </WorkflowSection>

        <WorkflowSection title="structure.md を作成する" subNumber="4.4">
          <WorkflowText>
            フロントエンド用のプロジェクト構成を定義する structure.md
            を作成します。tech.mdと同様に、React未経験者がディレクトリ設計を一から考えるのは非現実的なため、完成例をそのまま使います。以下をコピーして
            .kiro/steering/structure.md
            として保存してください。React経験者は自分で考えて書いても構いません。
          </WorkflowText>

          <WorkflowTipBox title="structure.mdには何が書いてあるのか？">
            <WorkflowText>
              structure.mdはAIに「ファイルをどこに配置するか」を伝えるファイルです。バックエンドのstructure.md（パッケージ構成）のフロントエンド版にあたります。
            </WorkflowText>
            <WorkflowText>
              Reactプロジェクトはディレクトリ構成のパターンが多く、AIに任せると毎回違う構成で生成されがちです。structure.mdで構成を固定しておくと、AIが一貫した場所にファイルを作成するようになります。
            </WorkflowText>
          </WorkflowTipBox>

          <WorkflowLockedCodeBlock password="Ht3mYk" showPassword>
            {`---
inclusion: always
---

# プロジェクト構成

## ディレクトリ構造

\`\`\`
team_todo_front/
├── public/                  # 静的ファイル
├── src/
│   ├── components/          # 再利用可能なUIコンポーネント
│   │   └── common/          # 汎用コンポーネント（ボタン、モーダル等）
│   ├── pages/               # ページコンポーネント（ルーティング単位）
│   ├── hooks/               # カスタムフック
│   ├── api/                 # API通信関連
│   │   ├── client.ts        # APIクライアント（共通設定）
│   │   └── queries/         # TanStack Queryのクエリ定義
│   ├── types/               # 型定義
│   ├── utils/               # ユーティリティ関数
│   ├── routes/              # ルーティング設定
│   ├── App.tsx              # アプリケーションルート
│   └── main.tsx             # エントリーポイント
├── .kiro/
│   └── steering/            # Steeringルール
├── docs/                    # ドキュメント（API仕様書、画面定義書等）
├── index.html
├── package.json
├── tsconfig.json
├── vite.config.ts
└── HELPME.md
\`\`\`

## 配置ルール

| ファイル種別 | 配置先 | 例 |
|------------|--------|-----|
| ページコンポーネント | \`src/pages/\` | \`TodoListPage.tsx\` |
| 部品コンポーネント | \`src/components/\` | \`TaskCard.tsx\` |
| 汎用コンポーネント | \`src/components/common/\` | \`ConfirmDialog.tsx\` |
| カスタムフック | \`src/hooks/\` | \`useTodos.ts\` |
| API通信 | \`src/api/queries/\` | \`todoQueries.ts\` |
| 型定義 | \`src/types/\` | \`todo.ts\` |
| ユーティリティ | \`src/utils/\` | \`dateFormat.ts\` |
| ルーティング設定 | \`src/routes/\` | \`index.tsx\` |

## 命名規則（ファイル）

- ページコンポーネント: \`{機能名}Page.tsx\`（例: \`TodoListPage.tsx\`）
- 部品コンポーネント: \`{コンポーネント名}.tsx\`（例: \`TaskCard.tsx\`）
- カスタムフック: \`use{機能名}.ts\`（例: \`useTodos.ts\`）
- API定義: \`{リソース名}Queries.ts\`（例: \`todoQueries.ts\`）
- 型定義: \`{リソース名}.ts\`（例: \`todo.ts\`）`}
          </WorkflowLockedCodeBlock>
        </WorkflowSection>

        <WorkflowSection title="設置状況の確認" subNumber="4.5">
          <WorkflowText>3つのSteeringファイルが正しく配置されていることを確認します。</WorkflowText>

          <WorkflowCodeBlock language="bash" code={`ls .kiro/steering/`} />

          <WorkflowText>以下の3ファイルが表示されればOKです。</WorkflowText>

          <WorkflowCodeBlock language="text" code={`product.md\nstructure.md\ntech.md`} />

          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              ポイント
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              product.mdは要件定義編で自分で書いたもの。tech.mdとstructure.mdは今回コピペしたものです。バックエンドのときは全て自分で考えて書きましたが、フロントエンドではReact未経験者が設計を一から考えるのは非現実的なため、完成例を提供しています。内容を読んで「こういうルールになっているんだな」と理解しておけば十分です。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション5: 現在のプロジェクト構成 */}
      <WorkflowSection title="現在のプロジェクト構成" id="section-5" number={5}>
        <WorkflowText>ここまでの作業で、プロジェクトは以下の状態になっています。</WorkflowText>

        <WorkflowDirectoryTree
          root="team_todo_front/"
          entries={[
            {
              name: '.kiro',
              color: '#1565c0',
              children: [
                {
                  name: 'steering',
                  children: [
                    { name: 'product.md', desc: 'バックエンドからコピー', highlight: '#e65100' },
                    { name: 'tech.md', desc: '完成例をコピペ', highlight: '#e65100' },
                    { name: 'structure.md', desc: '完成例をコピペ', highlight: '#e65100' },
                  ],
                },
              ],
            },
            {
              name: 'node_modules',
              defaultOpen: false,
            },
            {
              name: 'public',
              defaultOpen: false,
            },
            {
              name: 'src',
              children: [{ name: 'App.css' }, { name: 'App.tsx' }, { name: 'main.tsx' }],
            },
            { name: 'HELPME.md', desc: 'Kiroが生成', highlight: '#1976d2' },
            { name: 'index.html' },
            { name: 'package.json' },
            { name: 'tsconfig.json' },
            { name: 'vite.config.ts' },
          ]}
        />
      </WorkflowSection>

      {/* セクション6: Gitリポジトリを作成してプッシュする */}
      <WorkflowSection title="Gitリポジトリを作成してプッシュする" id="section-6" number={6}>
        <WorkflowText>ここまでの成果をGitで管理します。</WorkflowText>

        <WorkflowSection title="GitHubでリポジトリを作成する" subNumber="6.1">
          <WorkflowText>
            GitHubにログインし、右上の「+」→「New
            repository」をクリックして、以下の設定で作成します。
          </WorkflowText>

          <WorkflowTable
            headers={['項目', '設定値']}
            rows={[
              ['Repository name', 'team-todo-front'],
              [
                'Description',
                'Team TODO management app - Frontend (AI-driven development hands-on)',
              ],
              ['Visibility', 'Private（推奨）'],
              ['Initialize', 'チェックなし（README, .gitignore, license 全て不要）'],
            ]}
          />

          <WorkflowText>設定したら「Create repository」をクリックします。</WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="ローカルでGit初期化とコミット" subNumber="6.2">
          <WorkflowText>
            プロジェクトのルートディレクトリで以下のコマンドを実行します。
          </WorkflowText>

          <WorkflowCodeBlock
            language="bash"
            code={`git init\ngit add .\ngit commit -m "initial commit"`}
          />

          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              ポイント
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Viteが .gitignore を自動生成しているため、node_modules/ や dist/ は除外されます。git
              add . で余計なファイルがコミットされる心配はありません。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="リモートに接続してプッシュ" subNumber="6.3">
          <WorkflowText>
            GitHubで作成したリポジトリのURLを使って、リモートを設定しプッシュします。
          </WorkflowText>

          <WorkflowCodeBlock
            language="bash"
            code={`git remote add origin https://github.com/{あなたのユーザー名}/team-todo-front.git\ngit branch -M main\ngit push -u origin main`}
          />

          <WorkflowText>
            GitHubのリポジトリページをリロードして、ファイルが表示されていれば完了です。
          </WorkflowText>

          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              補足
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              以降のステップでも区切りのいいタイミングでコミット・プッシュする習慣をつけましょう。バックエンド編と同じです。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* 予告ボックス */}
      <WorkflowInfoBox type="preview">
        <Typography variant="body2" fontWeight={600}>
          次の記事: フロントエンド編 — API仕様書の取り込み
        </Typography>
        <WorkflowList
          items={[
            'バックエンドの仕様書をフロント向けSwagger（OpenAPI）に変換する',
            'api_specs_rule.mdを設置してAIで生成する',
            'フロントが欲しい情報を自分に都合の良い形でまとめる',
          ]}
        />
      </WorkflowInfoBox>

      {/* チェックリスト */}
      <WorkflowChecklist
        groups={[
          {
            title: 'チェックリスト',
            items: [
              'team_todo_front ディレクトリを作成し、Kiroで新しいワークスペースとして開いた',
              'KiroにVite + React + TypeScriptプロジェクトを作成してもらった',
              'MUI / React Router / TanStack Queryがインストールされている',
              'HELPME.mdがKiroによって生成されている',
              'npm run dev で開発サーバーが起動し、ブラウザで画面が表示される',
              '.kiro/steering/product.md にバックエンドのproduct.mdをコピーした',
              '.kiro/steering/tech.md を完成例からコピペして配置した',
              '.kiro/steering/structure.md を完成例からコピペして配置した',
              'GitHubにリポジトリを作成し、プッシュ完了した',
            ],
          },
        ]}
      />
    </HandsonArticleLayout>
  );
};

export default HandsonProjectSetup;
